import { type ReactNode, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Receipt, Users, LifeBuoy, Settings,
  ShieldCheck, LogOut, Menu, Bell, Bot,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Brand } from "@/components/site/brand";
import { DisclaimerBar } from "@/components/site/disclaimer-bar";
import { BtcTicker } from "@/components/site/btc-ticker";
import { NotificationsBell } from "@/components/app/notifications-bell";
import { toast } from "sonner";
import { useIsAdmin } from "@/lib/data-hooks";

const userNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/deposit", label: "Deposit", icon: ArrowDownToLine },
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpFromLine },
  { to: "/bots", label: "Trading Bots", icon: Bot },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/referrals", label: "Referrals", icon: Users },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);

  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();

  const signOut = async () => {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate({ to: "/auth", replace: true });
  };

  const SidebarContent = () => (
    <>
      <div className="px-4 py-5"><Brand /></div>
      <nav className="px-2 space-y-1 flex-1">
        {userNav.map(({ to, label, icon: Ic }) => {
          const active = pathname === to;
          return (
            <Link key={to} to={to} onClick={() => setOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"}`}>
              <Ic className="h-4 w-4" /> {label}
            </Link>
          );
        })}
        {adminLoading && (
          <div className="mt-6 px-3 py-2 text-xs text-muted-foreground">Checking admin access…</div>
        )}
        {isAdmin && (
          <>
            <div className="mt-6 px-3 text-[11px] uppercase tracking-widest text-muted-foreground">Admin</div>
            <Link to="/admin" onClick={() => setOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${pathname.startsWith("/admin") ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"}`}>
              <ShieldCheck className="h-4 w-4" /> Admin Dashboard
            </Link>
          </>
        )}
        {!isAdmin && !adminLoading && (
          <button
            onClick={async () => {
              const { error } = await supabase.rpc("claim_first_admin");
              if (error) { toast.error(error.message); return; }
              toast.success("You are now admin");
              qc.invalidateQueries({ queryKey: ["is-admin"] });
              navigate({ to: "/admin" });
            }}
            className="mt-6 w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground"
          >
            <ShieldCheck className="h-4 w-4" /> Claim admin (first user)
          </button>
        )}
      </nav>
      <div className="p-3">
        <button onClick={signOut} className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <DisclaimerBar />
      <div className="flex flex-1">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/5 bg-sidebar/60 backdrop-blur-xl">
          <SidebarContent />
        </aside>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-sidebar flex flex-col">
              <SidebarContent />
            </aside>
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="sticky top-[26px] z-30 flex h-16 items-center justify-between gap-3 border-b border-white/5 bg-background/70 backdrop-blur-xl px-4">
            <button className="lg:hidden p-2" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <div className="hidden sm:block flex-1"><BtcTicker compact /></div>
            <div className="flex items-center gap-2">
              <NotificationsBell />
              <div className="text-xs text-muted-foreground font-mono hidden md:inline">{new Date().toLocaleDateString()}</div>
            </div>
          </header>
          <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}